<script setup lang="ts">
import type { IChipVariant } from "@/components/ui/Chip/chip.interface"
import type { IPackageSubscription } from "@/types"
import { computed } from "vue"
import { Button, Chip, Expandable, Icon } from "@/components/ui"
import { useTranslation } from "@/composables"
import { PackageSubscriptionStatus } from "@/types"
import { getRenewalInfo } from "./util"

const { packageSubscription } = defineProps<{ packageSubscription: IPackageSubscription }>()

const { t } = useTranslation()

const usage = computed<{ percentage: number, isNearLimit: boolean, isAtLimit: boolean }>(() => {
  const used = packageSubscription?.eventsUsed ?? 0
  const max = packageSubscription?.maxEvents ?? 0
  const percentage = max > 0 ? Math.round((used / max) * 100) : 0

  return {
    percentage,
    isNearLimit: percentage >= 80,
    isAtLimit: used >= max && max > 0,
  }
})

const status = computed<{ variant: IChipVariant, label: string, color: string }>(() => {
  const statuses = {
    [PackageSubscriptionStatus.Active]: { variant: "success" as const, label: t("package_subscription_card.active"), color: "text-green-600" },
    [PackageSubscriptionStatus.Cancelled]: { variant: "warning" as const, label: t("package_subscription_card.cancelled"), color: "text-yellow-600" },
    [PackageSubscriptionStatus.Expired]: { variant: "danger" as const, label: t("package_subscription_card.expired"), color: "text-red-600" },
    [PackageSubscriptionStatus.Suspended]: { variant: "danger" as const, label: t("package_subscription_card.suspended"), color: "text-red-600" },
  }

  return statuses[packageSubscription?.status ?? PackageSubscriptionStatus.Active]
})
</script>

<template>
  <div class="border border-primary bg-white rounded-lg overflow-hidden w-full font-montserrat flex flex-col">
    <Expandable>
      <template #activator="{ toggle, isOpen, activatorAttrs }">
        <div class="flex justify-between items-center px-4 py-2 bg-primary text-white flex-wrap gap-2">
          <div class="flex items-center gap-2">
            <Icon icon="Box" size="lg" />
            <div class="flex flex-col">
              <h3 class="text-xl font-bold font-comfortaa">
                {{ packageSubscription.tierName }}
              </h3>
            </div>
          </div>
          <div class="flex gap-2 items-center justify-center">
            <Chip v-if="status" :label="status.label" :variant="status.variant" size="sm" />
            <Button v-bind="activatorAttrs" @click="toggle">
              <Icon icon="CircleChevronDown" :class="isOpen ? 'rotate-180' : 'rotate-0'" class="transition" />
              <span class="sr-only">{{ isOpen ? t("package_subscription_card.toggle_collapse") : t("package_subscription_card.toggle_expand") }}</span>
            </Button>
          </div>
        </div>
      </template>

      <div class="p-4 flex flex-col gap-4">
        <div class="flex flex-col gap-2">
          <div class="flex justify-between items-center text-sm">
            <span class="text-label">{{ t("package_subscription_card.events_used") }}</span>
            <span class="font-semibold text-primary">
              {{ packageSubscription.eventsUsed }} / {{ packageSubscription.maxEvents }}
            </span>
          </div>
          <label for="events-progress" class="sr-only">
            {{ t("package_subscription_card.events_used") }}: {{ packageSubscription.eventsUsed }} / {{ packageSubscription.maxEvents }}
          </label>
          <progress
            id="events-progress"
            :value="packageSubscription.eventsUsed"
            :max="packageSubscription.maxEvents"
            class="w-full h-3 rounded-full overflow-hidden appearance-none transition-all duration-500 ease-in-out [&::-webkit-progress-value]:bg-secondary [&::-moz-progress-bar]:bg-secondary [&::-webkit-progress-bar]:bg-gray-200 [&::-webkit-progress-bar]:rounded-full [&::-moz-progress-bar]:rounded-full"
          >
            {{ usage.percentage }}%
          </progress>
        </div>

        <div class="flex justify-between items-center flex-wrap gap-2">
          <div v-if="getRenewalInfo(packageSubscription)" class="flex items-center gap-2 text-sm">
            <Icon icon="Calendar" size="sm" class="text-gray-500" />
            <span class="text-gray-600">
              {{ getRenewalInfo(packageSubscription) }}
            </span>
          </div>
          <a href="https://guestpix.com/my-account/subscriptions/" class="text-link underline flex items-center gap-2" :aria-label="t('package_subscription_card.manage_subscription_aria_label')">
            {{ t("package_subscription_card.manage_subscription") }}
            <Icon icon="ArrowRight" size="sm" class="text-link" />
          </a>
        </div>
      </div>
    </Expandable>
  </div>
</template>
